import type { Post } from '../blogTypes'

export const costOfDormerWindow: Post = {
  slug: 'cost-of-dormer-window',
  title: 'Cost of a Dormer Window: 2025 Price Guide by Type and Size',
  metaTitle: 'Cost of a Dormer Window: 2025 Price Guide by Type and Size',
  description:
    'How much does a dormer window cost? See average price ranges for shed, gable, hip and flat dormers, what drives the price, and how to estimate your own.',
  excerpt:
    'Typical dormers cost between $15,000 and $80,000 installed. Here is what pushes a quote up or down, and how to budget before you call a contractor.',
  category: 'cost-planning',
  image: 'https://images.unsplash.com/photo-1763979021741-cfbe06355d7a?auto=format&fit=crop&w=1200&q=75',
  imageAlt: 'Dormer window on a shingled roof',
  datePublished: '2025-01-12',
  dateModified: '2025-03-04',
  readMinutes: 7,
  blocks: [
    {
      type: 'p',
      text: 'Most homeowners who ask about the cost of a dormer window are really asking about the cost of a dormer: the small framed structure that projects from a sloping roof and holds the window. The window itself is often the cheapest part. The framing, roof work, insulation and finishing are where the budget goes.',
    },
    {
      type: 'p',
      text: 'In the United States, a typical installed dormer costs **$15,000 to $80,000**. A small decorative dormer on an existing attic sits at the bottom of that range. A wide shed dormer that adds a full bedroom or bathroom sits at the top, and large custom builds in high-cost cities can go beyond it. If you want a number for your exact dimensions, run them through the [dormer calculator](/calculator) first and use this guide to understand the result.',
    },
    { type: 'h2', text: 'Average dormer cost by type' },
    {
      type: 'ul',
      items: [
        '**Eyebrow or decorative dormer:** $5,000 to $15,000. Very small, rarely adds usable floor space.',
        '**Gable (A-frame) dormer:** $15,000 to $45,000 for a single unit. Classic look, more complex roof framing.',
        '**Hip dormer:** $20,000 to $50,000. Three sloping roof faces make it harder to build and flash.',
        '**Shed dormer:** $25,000 to $80,000. Simple single-slope roof, the best value per square foot of added headroom.',
        '**Flat-roof dormer:** $20,000 to $60,000. Modern look, but needs careful waterproofing and drainage.',
      ],
    },
    { type: 'h2', text: 'What drives the price' },
    {
      type: 'p',
      text: 'Five factors explain most of the difference between a low and a high quote. Size is the biggest: cost scales with floor area and with the surface you must clad, insulate and roof. Complexity comes second, since a gable or hip roof takes longer to frame than a shed roof. Third is the finish level, from basic vinyl siding to slate or standing-seam metal. Fourth is the structural work: cutting a large opening in the roof needs a header beam and often temporary support. Fifth is local labor cost, which varies enormously between regions.',
    },
    { type: 'h2', text: 'Cost per square foot' },
    {
      type: 'p',
      text: 'Contractors often quote dormers at **$100 to $250 per square foot of new floor area**. A small 8 by 6 foot dormer has 48 square feet of floor, so a budget of roughly $5,000 to $12,000 would be unrealistic once you include the roof opening, electrics and finishing. The per-square-foot figure works best on larger dormers of 100 square feet or more, where fixed costs spread out.',
    },
    { type: 'h2', text: 'Costs people forget to budget for' },
    {
      type: 'ul',
      items: [
        'Architect or structural engineer drawings, often 5 to 10 percent of the build cost.',
        'Planning and building permit fees. See our [planning permission guide](/blog/dormer-planning-permission-guide).',
        'Scaffolding and temporary roof protection while the roof is open.',
        'Extending electrics, heating and ventilation into the new space.',
        'Interior finishing: flooring, paint, trim and fitted storage.',
        'A contingency of at least 10 percent. Old roofs hide surprises such as rotten rafters.',
      ],
    },
    { type: 'h2', text: 'How to keep the cost down' },
    {
      type: 'p',
      text: 'Choose a shed dormer if your priority is space, because it gives the most headroom for the least framing. Keep the dormer on the rear or side slope when permitted: it is cheaper to hide and often easier to approve. Pick mid-range cladding that matches the house instead of premium materials. Finally, group the work: building a dormer at the same time as a re-roof saves scaffolding and labor.',
    },
    {
      type: 'cta',
      title: 'Get your own dormer estimate',
      text: 'Enter your width, depth, height, pitch and material to see a materials list and a cost range in seconds.',
    },
    { type: 'h2', text: 'Bottom line' },
    {
      type: 'p',
      text: 'There is no single price for a dormer window, because a dormer is a small construction project, not a product. Use the ranges above for early budgeting, calculate your own numbers, and then collect at least three written quotes. Compare them line by line so you know what each contractor includes.',
    },
  ],
}
